import { axiosClient } from "@/lib/axios";
import { queryClient } from "@/lib/react-query";
import { HttpError } from "@/types/error";
import { PublicRecording, WithPagination } from "@classview/shared-types";
import {
  InfiniteData,
  UseMutationOptions,
  useMutation,
} from "@tanstack/react-query";

import { getRecordingQueryKey } from "./get-recording";

const markRecordingAsPermanent = async ({
  orgId,
  id,
}: {
  orgId: string;
  id: string;
}): Promise<string> => {
  const response = await axiosClient.post(
    `/${orgId}/recordings/${id}/mark-as-permanent`,
  );
  return response.data;
};

/**
 * Updates a recording in the infinite query cache
 *
 * This utility function is used to update a specific recording within the paginated
 * infinite query cache. It searches through all pages and updates the recording
 * that matches the given recordingId.
 *
 * @param organisationId - The organisation ID to target the correct query
 * @param recordingId - The ID of the recording to update
 * @param updater - Function that takes the current recording and returns the updated recording
 *
 * @example
 * ```typescript
 * // Update a recording to mark it as permanent
 * updateRecordingInInfiniteQuery(organisationId, recordingId, (recording) => ({
 *   ...recording,
 *   isPermanent: true,
 *   isVisible: true,
 *   daysLeftToDelete: undefined,
 *   expirationDate: undefined,
 * }));
 *
 * // Replace with completely new data
 * updateRecordingInInfiniteQuery(organisationId, recordingId, () => updatedRecording);
 * ```
 */
function updateRecordingInInfiniteQuery(
  organisationId: string,
  recordingId: string,
  updater: (recording: PublicRecording) => PublicRecording,
) {
  // Update all possible infinite query variations
  queryClient.setQueriesData(
    { queryKey: ["recordings", organisationId] },
    (oldData: InfiniteData<WithPagination<PublicRecording>, unknown>) => {
      if (!oldData?.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page: WithPagination<PublicRecording>) => ({
          ...page,
          items: page.items.map((recording: PublicRecording) =>
            recording.id === recordingId ? updater(recording) : recording,
          ),
        })),
      };
    },
  );
}

function useMarkRecordingAsPermanent(
  mutationOptions?: Partial<
    UseMutationOptions<string, HttpError, { id: string; orgId: string }>
  >,
) {
  return useMutation({
    mutationFn: ({ id, orgId }) => markRecordingAsPermanent({ id, orgId }),
    onSuccess: (data, variables, context) => {
      const { id: recordingId, orgId: organisationId } = variables;

      // Update the infinite query cache
      updateRecordingInInfiniteQuery(
        organisationId,
        recordingId,
        (recording) => ({
          ...recording,
          isPermanent: true,
          isVisible: true,
          daysLeftToDelete: null,
          expirationDate: null,
        }),
      );

      // Update the single recording query cache
      queryClient.setQueryData(
        getRecordingQueryKey(organisationId, recordingId),
        (old: PublicRecording) => {
          if (!old) return old;
          return {
            ...old,
            isPermanent: true,
            isVisible: true,
            daysLeftToDelete: null,
            expirationDate: null,
          };
        },
      );

      // Call the original onSuccess if provided
      mutationOptions?.onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
}

export { updateRecordingInInfiniteQuery, useMarkRecordingAsPermanent };
